import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronRight, Brain } from 'lucide-react';
import { ProcessLogEntry, PROCESS_KIND_LABELS } from '@/components/agent/types';
import { stripThinkingAndToolCallTags } from '../tagParser';

// ============================================================
// 流式场景下不完整 markdown 结构检测与修复
// 在流式渲染时，部分 markdown 结构可能只渲染了开头（表格行、列表项、
// 代码块等），导致视觉上出现"不完整→完整"的闪烁。
// 本工具将检测这些不完整结构，并将其转回纯文本行。
// ============================================================

// 🔴 M1-修复: isIncompleteTableRow — 与 isPotentialTableRow 判定标准一致
// 支持标准格式（以 | 开头）和宽松格式（无前导 |，含多个 |）
const isIncompleteTableRow = (line: string): boolean => {
  const t = line.trim();
  // 标准格式：以 | 开头但不以 | 结尾
  if (t.startsWith('|') && !t.endsWith('|')) return true;
  // 宽松格式：包含至少 2 个 | 但不以 | 结尾（可能是被截断的无前导 | 表格行）
  const pipeCount = (t.match(/\|/g) || []).length;
  if (pipeCount >= 2 && !t.endsWith('|')) {
    const segments = t.split('|').filter(s => s.trim().length > 0);
    if (segments.length >= 2) return true;
  }
  return false;
};

/**
 * 流式场景下处理不完整 Markdown 结构。
 *
 * 核心策略：**truncate（截断）而非 replace（替换）**
 * - 不完整的链接/标签/表格行 → 直接截断，暂不显示
 * - 下一帧更多 chunk 到达后结构变完整 → 自然显示
 * - 已显示的内容永远不会被移除或替换，彻底消除"内容出现→消失→再出现"的闪烁
 *
 * 补全类处理（不会导致闪烁，保留原逻辑）：
 *   - 未闭合的加粗 ** → 补 **
 *   - 未闭合的斜体 * → 补 *
 *   - 未闭合的行内代码 ` → 补 `
 *   - 未闭合代码块 / 表格 → 由 _renderFormattedMessageInnerCore 末尾统一刷新
 */
const stripIncompleteMarkdown = (text: string): string => {
  // ---- 1. 补全类处理：闭合未完成的内联标记（不改变行数/行结构）
  //    代码块/表格：_renderFormattedMessageInnerCore 末尾已处理未闭合刷新
  //    这里只处理内联级别的 ** / * / ` （不增加/删除行，不加 \n）

  let processed = text;

  // 追踪代码块状态，避免对 ``` 内的内容做内联标记计数
  const countOutsideCodeBlocks = (text2: string, marker: string | RegExp): number => {
    let inCb = false;
    let count = 0;
    for (const line of text2.split('\n')) {
      if (line.trimStart().startsWith('```')) { inCb = !inCb; continue; }
      if (inCb) continue;
      if (typeof marker === 'string') {
        count += (line.match(new RegExp(marker.replace(/\*/g, '\\*'), 'g')) || []).length;
      } else {
        count += (line.match(marker) || []).length;
      }
    }
    return count;
  };

  // 未闭合粗体 **
  if (countOutsideCodeBlocks(processed, '**') % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '**';
  }

  // 未闭合斜体 *（先排除 ** 后计数）
  if (countOutsideCodeBlocks(processed.replace(/\*\*/g, ''), /\*/) % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '*';
  }

  // 未闭合内联代码 `
  if (countOutsideCodeBlocks(processed, /`/) % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '`';
  }

  // ---- 2. 截断类处理（消除闪烁的关键） ----
  // 不完整的链接/标签/表格行 → 截断到不完整结构之前
  // 已显示的内容保持不变，不完整部分等下一帧自然补全

  // 不完整链接 [text](url → 截断到 [text](url 之前
  const twoLevelParenPartial = '(?:[^()]|\\((?:[^()]|\\([^()]*\\))*\\))*';
  const incompleteLinkRegex = new RegExp(`\\[([^\\]]*)\\]\\(${twoLevelParenPartial}$`);
  const incompleteLinkMatch = processed.match(incompleteLinkRegex);
  if (incompleteLinkMatch) {
    // 截断到不完整链接之前，而非替换为纯文本
    processed = processed.replace(new RegExp(`\\[([^\\]]*)\\]\\(${twoLevelParenPartial}$`), '');
  }

  // 不完整标签 [text → 截断到 [text 之前
  const incompleteLabelRegex = /\[([^[\]]*)$/;
  const incompleteLabelMatch = processed.match(incompleteLabelRegex);
  if (incompleteLabelMatch) {
    processed = processed.replace(/\[([^[\]]*)$/, '');
  }

  // ---- 3. 不完整表格行 → 截断到不完整行之前 ----
  // 保留已完成的表格行，只截断不完整的最后一行
  const lines = processed.split('\n');
  const result: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (isIncompleteTableRow(line)) {
      // 🔴 核心修复：不完整表格行 → 截断到此行之前
      // 已推入 result 的行（表头、分隔行、完整数据行）保持不变
      // 不完整行不追加 → 下一帧 chunk 补全后自然显示
      // 不再使用 <!-- TABLE_LOADING --> 占位符（避免"出现→消失→再出现"闪烁）
      break;
    }

    result.push(line);
    i++;
  }

  return result.join('\n');
};

const parseInlineStyles = (text: string): React.ReactNode[] => {
  // P0(v8): 使用 [^\]]* 和 [^)]* 替代 .*? 非贪婪匹配
  // 原正则 \[.*?\]\(.*?\) 中 .*? 遇到 URL 中的 ) 会提前截断，导致链接不完整
  // 改用字符类排除法：[^\]]* 匹配非 ] 字符，[^)]* 匹配非 ) 字符
  // 🔴 P2-6: 升级链接正则为两层嵌套括号支持
  // 模式: (?:[^()]|\((?:[^()]|\([^()]*\))*\))* 允许 URL 内含 article(1)(2) 或 func(a(b)) 这类结构
  const twoLevelParen = '(?:[^()]|\\((?:[^()]|\\([^()]*\\))*\\))*';
  const linkPattern = `\\[[^\\]]*\\]\\(${twoLevelParen}\\)`;
  const bt = '`';
  // 🔴 P5: 裸 URL 自动链接检测 — 匹配 http(s):// 开头的 URL
  // 模式: https?:// 后接 URL 允许的字符（排除空格、中英文标点作为结尾符）
  // 用 (?:[^\s<>\"'`])+ 匹配 URL 主体，再手动排除末尾的中文标点和括号
  const bareUrlPattern = 'https?:\\/\\/[^\\s<>"\'`)]+(?:\\([^\\s)]*\\)|[^\\s<>"\'`).,;!?])*';
  const splitRegex = new RegExp(`(\\*\\*.*?\\*\\*|${linkPattern}|${bt}[^${bt}]+${bt}|${bareUrlPattern})`, 'g');
  const parts = text.split(splitRegex);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <span key={i} className="text-white font-bold mx-0.5">{part.slice(2, -2)}</span>;
    }
    // P2: 行内代码支持（`code` → 灰色背景等宽字体）
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={i} className="px-1.5 py-0.5 rounded bg-white/[0.08] text-[11px] text-cyan-300/90 font-mono mx-0.5">
          {part.slice(1, -1)}
        </code>
      );
    }
    // 🔴 P2-6: 同步链接匹配正则为两层嵌套括号支持，与 split 正则保持一致
    const linkMatch = part.match(/^\[([^\]]*)\]\(((?:[^()]|\((?:[^()]|\([^()]*\))*\))*)\)$/);
    if (linkMatch) {
      const [, label, url] = linkMatch;
      // 🔴 P2-2: URL 协议安全验证 — 只允许 http/https/ftp 协议
      // 防止 javascript:、data:、vbscript: 等危险协议在链接中被执行
      const isSafeUrl = /^https?:\/\//i.test(url) || /^ftp:\/\//i.test(url);
      if (!isSafeUrl) {
        // 不安全协议 → 降级为纯文本显示
        return <span key={i}>{label}</span>;
      }
      // 🔴 P2-4: 当链接标签是完整 URL 时，截断为只显示域名
      // AI 偶尔输出 [https://en.wikipedia.org/wiki/VeryLongTitle](URL) 格式
      // 显示完整 URL 作为标签文字太长，提取域名作为显示文本
      let displayLabel = label;
      if (/^https?:\/\//i.test(label) && label.length > 30) {
        try {
          const host = new URL(label).hostname;
          displayLabel = host.replace(/^www\./, '');
        } catch {
          // URL 解析失败，保留原始标签
        }
      }
      // 来源链接样式 — 统一使用灰圆框白字 pill/badge 样式（表格内外一致）
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-1.5 py-0.1 rounded-l-full rounded-r-full text-[10px] text-white/90 bg-white/[0.1] hover:bg-white/[0.15] border border-white/[0.06] transition-colors align-middle no-underline select-none"
          onClick={(e) => e.stopPropagation()}
          title={url}
        >
          {displayLabel}
        </a>
      );
    }
    // 🔴 P5: 裸 URL 检测（AI 可能直接输出 https://example.com 而非 [label](URL) 格式）
    // 检测条件: 以 http:// 或 https:// 开头，且不是已被 [label](URL) 匹配的形式
    const bareUrlMatch = part.match(/^(https?:\/\/[^\s<>\"'`)]+(?:\([^\s)]*\)|[^\s<>\"'`).,;!?])*)$/);
    if (bareUrlMatch) {
      const url = bareUrlMatch[1];
      // 从 URL 提取域名作为显示文本（限制长度，过长则截断）
      let displayLabel = url;
      try {
        const host = new URL(url).hostname;
        displayLabel = host.replace(/^www\./, '');
        if (displayLabel.length > 30) {
          displayLabel = displayLabel.slice(0, 28) + '...';
        }
      } catch {
        // URL 解析失败，使用前 30 个字符作为显示文本
        displayLabel = url.length > 30 ? url.slice(0, 28) + '...' : url;
      }
      // 裸 URL 统一使用灰圆框白字 pill 样式
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-1.5 py-0.1 rounded-l-full rounded-r-full text-[10px] text-white/90 bg-white/[0.1] hover:bg-white/[0.15] border border-white/[0.06] transition-colors align-middle no-underline select-none"
          onClick={(e) => e.stopPropagation()}
          title={url}
        >
          {displayLabel}
        </a>
      );
    }
    return part;
  });
};

export const useTypewriter = (text: string, speed: number = 15, isActive: boolean = true) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const abortRef = useRef(false);
  const rafRef = useRef<number>(0);
  const indexRef = useRef(0);
  const lastTextRef = useRef('');

  useEffect(() => {
    if (!isActive) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }
    
    abortRef.current = false;
    
    if (text.startsWith(lastTextRef.current) && text.length > lastTextRef.current.length) {
      indexRef.current = lastTextRef.current.length;
      setDisplayedText(lastTextRef.current);
    } else {
      indexRef.current = 0;
      setDisplayedText('');
    }
    
    setIsComplete(false);
    lastTextRef.current = text;
    let lastTime = 0;

    const tick = (timestamp: number) => {
      if (abortRef.current) {
        setDisplayedText(text);
        setIsComplete(true);
        return;
      }
      if (timestamp - lastTime >= speed) {
        lastTime = timestamp;
        if (indexRef.current < text.length) {
          setDisplayedText(text.slice(0, indexRef.current + 1));
          indexRef.current++;
        } else {
          setIsComplete(true);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [text, speed, isActive]);

  const abort = useCallback(() => { abortRef.current = true; }, []);
  return { displayedText, isComplete, abort };
};

const InlineThinkBlock: React.FC<{ content: string }> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = content.split('\n')[0].slice(0, 60) + (content.length > 60 ? '...' : '');

  return (
    <div className="my-2 rounded-lg border border-purple-500/20 bg-purple-500/[0.04] overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-purple-500/[0.06] transition-colors"
      >
        {isExpanded ? (
          <ChevronDown size={13} className="text-purple-400 shrink-0" />
        ) : (
          <ChevronRight size={13} className="text-purple-400 shrink-0" />
        )}
        <Brain size={13} className="text-purple-400 shrink-0" />
        <span className="text-[11px] text-purple-300 font-medium">思考过程</span>
        {!isExpanded && (
          <span className="text-[10px] text-gray-500 truncate ml-1">{preview}</span>
        )}
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1">
          <div className="text-[11px] text-gray-400 leading-relaxed whitespace-pre-wrap">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * @param text - 要渲染的文本
 * @param skipIncomplete - 流式场景下=true：对不完整的 markdown 结构（表格、代码块等）
 *                        先转回纯文本，避免视觉闪烁。历史消息传 false 以保留原始格式。
 */
export const renderFormattedMessage = (text: string, skipIncomplete = false) => {
  // P2修复：stripThinkingAndToolCallTags 已清除所有 [THINK]/[COLLAB_STATUS] 标签，
  // 后续的标签分割正则永远不会匹配，属于死代码，已移除。
  // P6(v6): preserveWhitespace=true 保留换行符，避免 markdown 格式被破坏；
  //          最后显式 .trim() 只清理首尾空白，不影响内部结构。
  let cleanedText = stripThinkingAndToolCallTags(text, true).trim();

  // P6-1: 流式进行中：把不完整表格/代码块转回纯文本，防止"闪烁"
  if (skipIncomplete) {
    cleanedText = stripIncompleteMarkdown(cleanedText);
  }

  return renderFormattedMessageInner(cleanedText);
};

const renderFormattedMessageInner = (text: string) => {
  // P1(v8): 外层 try-catch 防止任何渲染异常导致整个组件崩溃
  // 流式场景中文本不完整可能触发各种边界异常，捕获后降级为纯文本
  try {
    return _renderFormattedMessageInnerCore(text);
  } catch {
    // 降级：按换行分段显示纯文本，避免 SummaryErrorBoundary 捕获异常后闪烁
    const fallbackLines = text.split('\n');
    return (
      <div className="space-y-0.5 select-text cursor-text">
        {fallbackLines.map((line, i) => (
          <div key={`fb-${i}`} className="text-[12px] text-gray-300 leading-relaxed">
            {line || '\u00A0'}
          </div>
        ))}
      </div>
    );
  }
};

const _renderFormattedMessageInnerCore = (text: string) => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableKey = '';
  // P2: 代码块状态
  let inCodeBlock = false;
  let codeBlockKey = '';
  let codeBlockLang = '';
  let codeBlockLines: string[] = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const headerRow = tableRows[0];
    const bodyRows = tableRows.slice(1);
    elements.push(
      <div key={tableKey} className="my-3 overflow-x-auto rounded-lg border border-white/10 shadow-sm">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-white/[0.06]">
              {headerRow.map((cell, ci) => (
                <th key={ci} className="px-3 py-2 text-left text-gray-300 font-semibold border-b border-white/10 whitespace-nowrap">
                  {parseInlineStyles(cell.trim())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, ri) => (
              <tr key={ri} className="border-b border-white/5 last:border-0 hover:bg-white/[0.04] transition-colors">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-gray-400">
                    {parseInlineStyles(cell.trim())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    inTable = false;
  };

  // P1修复：分隔行必须同时包含 - 和 |，避免 --- 水平线被误判为表格分隔行
  const separatorRegex = /^\|?[\s:]*-[\s\-:|]*\|[\s\-:|]*$/;
  // 🔴 P2-修复：放宽表格起始行检测，支持 GFM 表格常见格式
  // - 标准格式: "| Header | Header |" （原已支持）
  // - 无首尾竖线: "Header | Header | Header" （AI 较常输出）
  // 判定条件: 至少包含 2 个 | 且分割出 ≥2 个非空段，避免普通文本（如"选项A | 选项B"）误判
  const isPotentialTableRow = (text: string): boolean => {
    // 标准格式：| Cell | Cell |
    if (/^\|\s*\S[^|]*\s*\|/.test(text)) return true;
    // 宽松格式：Cell | Cell | Cell（至少2个|，分割出≥2个非空段）
    const pipeCount = (text.match(/\|/g) || []).length;
    if (pipeCount >= 2) {
      const segments = text.split('|').filter(s => s.trim().length > 0);
      if (segments.length >= 2) return true;
    }
    return false;
  };

  // 判断一行是否像表格分隔行（如 "|---|---|" 或 "---:|---:|"）
  const isSeparatorRow = (text: string): boolean => {
    return separatorRegex.test(text.trim());
  };

  // 判断一行是否像表格数据行（进入表格模式后的宽松判定）
  const isTableDataRow = (text: string): boolean => {
    // 标准 Markdown 表格行
    if (/^\|\s*\S[^|]*\|/.test(text)) return true;
    const pipeCount = (text.match(/\|/g) || []).length;
    if (pipeCount >= 2) {
      const segments = text.split('|').filter(s => s.trim().length > 0);
      return segments.length >= 2;
    }
    return false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const key = `line-${i}`;
    const trimmed = line.trim();

    if (inTable && separatorRegex.test(trimmed)) {
      continue;
    }

    // 进入表格模式的判定：首次需要包含至少2个 | 且分割出≥2个非空段
    // 已在表格内时：允许更宽松的判定以兼容 AI 可能的格式变化
    const isTableRow = inTable ? isTableDataRow(trimmed) : isPotentialTableRow(trimmed);
    if (isTableRow && !trimmed.startsWith('#')) {
      // 🔴 P2-修复：放宽后仍需防误判 — 如果首行只有1个管道符且非标准格式，按普通段落处理
      // 注意：isPotentialTableRow 已要求 pipeCount >= 2，这里的防御主要针对 isTableDataRow 的宽松逻辑
      const pipeCount = (trimmed.match(/\|/g) || []).length;
      if (!inTable && pipeCount < 2) {
        // 首行管道符不足2个，按普通段落处理（此分支在 isPotentialTableRow 下实际不会触发，仅作防御）
      } else {
        const cleanCells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
        if (!inTable) {
          inTable = true;
          tableKey = key;
        }
        tableRows.push(cleanCells);
        continue;
      }
    } else if (inTable) {
      // P1: 表格区域内遇到空行时，跳过而非立即刷新表格
      // （AI 输出的表格行间可能存在空行，不应截断表格）
      if (!trimmed) {
        continue;
      }
      flushTable();
    }

    if (!trimmed) {
      elements.push(<div key={key} className="h-3" />);
      continue;
    }

    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={key} className="text-base font-bold text-white mt-5 mb-3 border-b border-white/10 pb-2.5">
          {parseInlineStyles(line.replace(/^#\s/, ''))}
        </h1>
      );
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key} className="text-sm font-bold text-white mt-4 mb-2 flex items-center gap-2">
          <span className="w-1.5 h-4 bg-gray-500 rounded-full inline-block" />
          {parseInlineStyles(line.replace(/^##\s/, ''))}
        </h2>
      );
      continue;
    }

    if (line.startsWith('### ')) {
      const content = line.replace(/^###\s/, '');
      elements.push(
        <h3 key={key} className="text-[13px] font-semibold text-gray-200 mt-3 mb-1.5 flex items-center gap-1.5">
          <span className="text-gray-400">•</span>
          {parseInlineStyles(content)}
        </h3>
      );
      continue;
    }

    if (line.startsWith('#### ')) {
      const content = line.replace(/^####\s/, '');
      elements.push(
        <h4 key={key} className="text-[12px] font-semibold text-gray-300 mt-2 mb-1">{parseInlineStyles(content)}</h4>
      );
      continue;
    }

    if (line.startsWith('- ')) {
      elements.push(
        <div key={key} className="flex items-start gap-2 ml-1">
          <span className="text-gray-400 mt-[5px] text-[8px] flex items-center justify-center w-3 h-3 rounded-full bg-white/[0.06]">•</span>
          <span className="text-[12px] text-gray-300 leading-relaxed">{parseInlineStyles(line.replace(/^-\s/, ''))}</span>
        </div>
      );
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <div key={key} className="flex items-start gap-2 ml-1">
            <span className="text-gray-300 text-[11px] mt-[1px] bg-white/[0.06] w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {match[1]}
            </span>
            <span className="text-[12px] text-gray-300 leading-relaxed">{parseInlineStyles(match[2])}</span>
          </div>
        );
      }
      continue;
    }

    if (line.startsWith('> ')) {
      elements.push(
        <div key={key} className="pl-4 border-l-2 border-gray-500 bg-white/[0.02] rounded-r-lg py-2 my-2 text-[12px] text-gray-400 italic">
          {parseInlineStyles(line.replace(/^>\s/, ''))}
        </div>
      );
      continue;
    }

    if (line.startsWith('```')) {
      // P2: 代码块开始/结束标记
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeBlockKey = key;
        codeBlockLang = line.replace(/^```\s*/, '').trim();
        codeBlockLines = [];
      } else {
        // 代码块结束，渲染完整代码块
        inCodeBlock = false;
        elements.push(
          <div key={codeBlockKey} className="my-2 rounded-lg border border-white/10 bg-black/30 overflow-hidden">
            {codeBlockLang && (
              <div className="px-3 py-1 text-[10px] text-gray-500 border-b border-white/5 bg-white/[0.02] font-mono">
                {codeBlockLang}
              </div>
            )}
            <pre className="px-3 py-2 text-[11px] text-gray-300 font-mono overflow-x-auto leading-relaxed">
              {codeBlockLines.join('\n')}
            </pre>
          </div>
        );
      }
      continue;
    }

    // P2: 代码块内容收集
    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    elements.push(
      <div key={key} className="text-[12px] text-gray-300 leading-relaxed">{parseInlineStyles(line)}</div>
    );
  }

  if (inTable) {
    flushTable();
  }

  // P2: 处理未闭合的代码块（流式场景中代码块可能尚未结束）
  if (inCodeBlock && codeBlockLines.length > 0) {
    elements.push(
      <div key={codeBlockKey} className="my-2 rounded-lg border border-white/10 bg-black/30 overflow-hidden">
        {codeBlockLang && (
          <div className="px-3 py-1 text-[10px] text-gray-500 border-b border-white/5 bg-white/[0.02] font-mono">
            {codeBlockLang}
          </div>
        )}
        <pre className="px-3 py-2 text-[11px] text-gray-300 font-mono overflow-x-auto leading-relaxed">
          {codeBlockLines.join('\n')}
        </pre>
      </div>
    );
  }

  return <div className="space-y-0.5 select-text cursor-text">{elements}</div>;
};

export const ThinkingIndicator: React.FC<{ seconds: number }> = ({ seconds }) => (
  <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-1">
    <span>思考了 {seconds} 秒</span>
    <ChevronDown size={10} className="rotate-[-90deg]" />
  </div>
);

export const TypewriterTextBlock: React.FC<{
  text: string;
  speed?: number;
  className?: string;
  skipAnimation?: boolean;
}> = ({ text, speed = 12, className, skipAnimation = false }) => {
  const { displayedText, isComplete } = useTypewriter(text, speed, !skipAnimation);
  return (
    <div className={className}>
      {renderFormattedMessage(displayedText, false)}
      {!isComplete && <span className="inline-block w-[2px] h-[12px] bg-cyan-400 ml-0.5 animate-pulse" />}
    </div>
  );
};

const COLLAB_PHASE_STYLES: Record<string, { bg: string; border: string; icon: string; textColor: string }> = {
  calling: { bg: 'bg-blue-500/[0.08]', border: 'border-blue-500/30', icon: '📡', textColor: 'text-blue-300' },
  accepted: { bg: 'bg-cyan-500/[0.08]', border: 'border-cyan-500/30', icon: '🤝', textColor: 'text-cyan-300' },
  executing: { bg: 'bg-amber-500/[0.08]', border: 'border-amber-500/30', icon: '⚡', textColor: 'text-amber-300' },
  completed: { bg: 'bg-emerald-500/[0.08]', border: 'border-emerald-500/30', icon: '✅', textColor: 'text-emerald-300' },
  returning: { bg: 'bg-violet-500/[0.08]', border: 'border-violet-500/30', icon: '🔄', textColor: 'text-violet-300' },
  error: { bg: 'bg-rose-500/[0.08]', border: 'border-rose-500/30', icon: '❌', textColor: 'text-rose-300' },
};

export const CollabStatusCard: React.FC<{
  phase: string;
  content: string;
}> = ({ phase, content }) => {
  const style = COLLAB_PHASE_STYLES[phase] || COLLAB_PHASE_STYLES.executing;
  const parts = content.split('|');

  const renderCalling = () => {
    const caller = parts[0] || '';
    const action = parts[1] || '发起调用';
    const target = parts[2] || '';
    const taskDesc = parts[3] || '';
    return (
      <>
        <span className="text-white font-medium">{caller}</span>
        <span className="text-gray-400">{action}</span>
        <span className="text-cyan-300 font-medium">{target}</span>
        {taskDesc && (
          <div className="w-full mt-1 pl-0">
            <span className="text-gray-500 text-[10px] leading-relaxed">{taskDesc}</span>
          </div>
        )}
      </>
    );
  };

  const renderDefault = () => {
    const agent = parts[0] || '';
    const status = parts[1] || '';
    const detail = parts[2] || '';
    return (
      <>
        <span className="text-white font-medium">{agent}</span>
        <span className={`font-medium ${style.textColor}`}>{status}</span>
        {detail && (
          <div className="w-full mt-1 pl-0">
            <span className="text-gray-500 text-[10px] leading-relaxed">{detail}</span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`my-1.5 rounded-lg border ${style.border} ${style.bg} px-3 py-2.5 flex items-start gap-2.5`}>
      <span className="text-base mt-0.5 shrink-0">{style.icon}</span>
      <div className="flex items-center gap-1.5 text-[11px] flex-wrap min-w-0 flex-1">
        {phase === 'calling' && parts.length >= 3 ? renderCalling() : renderDefault()}
      </div>
    </div>
  );
};

export const ProcessLogPanel: React.FC<{
  logs: ProcessLogEntry[];
  title?: string;
}> = ({ logs, title = '处理过程' }) => {
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  if (!logs.length) return null;

  return (
    <div className="space-y-2">
      <div className="text-[11px] text-slate-500">{title}</div>
      <div className="pl-3 ml-1 border-l border-white/10 space-y-2">
        {logs.map((log) => {
          const hasDetail = Boolean(log.detail);
          const isExpanded = expandedMap[log.id] ?? log.status === 'active';

          return (
            <div key={log.id} className="relative min-w-0">
              <div className={`absolute -left-[16px] top-1.5 h-1.5 w-1.5 rounded-full ${log.status === 'error' ? 'bg-rose-400' : log.status === 'active' ? 'bg-cyan-400 animate-pulse' : 'bg-white/35'}`} />
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  disabled={!hasDetail}
                  onClick={() => { if (hasDetail) setExpandedMap((prev) => ({ ...prev, [log.id]: !isExpanded })); }}
                  className={`flex items-center gap-1.5 text-left ${hasDetail ? 'hover:opacity-90' : 'cursor-default'}`}
                >
                  {hasDetail ? (
                    <ChevronDown size={11} className={`mt-[1px] text-slate-600 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                  ) : (
                    <span className="inline-block w-[11px]" />
                  )}
                  <span className={`text-[11px] ${log.status === 'error' ? 'text-rose-300' : log.status === 'active' ? 'text-cyan-300' : 'text-white/90'}`}>
                    {log.title}
                  </span>
                </button>
                <span className="pt-[1px] text-[10px] text-slate-600">{PROCESS_KIND_LABELS[log.kind]}</span>
              </div>
              {hasDetail && isExpanded && (
                <div className="mt-0.5 ml-[18px] text-[11px] leading-5 text-slate-500 whitespace-pre-wrap">{log.detail}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
